import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { RoleService, Role } from 'services/role.service';
import AuthService from 'services/auth.service';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormResultMessage from './FormResultMessage';

const NewUserForm: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleNames, setSelectedRoleNames] = useState<string[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<'success' | 'error'>(
    'success'
  );
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [activationCode, setActivationCode] = useState<boolean>(false);

  useEffect(() => {
    const fetchRoles = async () => {
      const rolesData = await RoleService.getAllRoles();
      setRoles(rolesData);

      // Set role with id = 3 as selected
      const roleWithId3 = rolesData.find(role => role.id === 3);
      if (roleWithId3) {
        setSelectedRoleNames([roleWithId3.rolename]);
      }
    };

    fetchRoles();
  }, []);

  //To check information registered
  const formik = useFormik({
    initialValues: {
      name: '',
      lastname: '',
      username: '',
      email: '',
      password: '',
      activation_code: 0,
      roles: selectedRoleNames
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(3, 'First name must be at least 3 characters')
        .required('First name is required'),
      lastname: Yup.string()
        .min(3, 'Last name must be at least 3 characters')
        .required('Last name is required'),
      username: Yup.string()
        .min(10, 'Username must be at least 10 characters')
        .max(50, 'Username must be less than 50 characters')
        .test(
          'no-at-sign',
          '"@" is not allowed in username',
          value => !value || !/@/.test(value)
        )
        .required('Username is required'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      password: Yup.string()
        .min(10, 'Password must be at least 10 characters')
        .required('Password is required')
    }),
    onSubmit: async (values, { resetForm }) => {
      //const dataToSend = {
      //  ...values,
      //  roles: selectedRoleNames
      //};
      try {
        await AuthService.newaccount(
          values.username,
          values.name,
          values.lastname,
          values.email,
          values.password,
          activationCode ? 1 : 0,
          selectedRoleNames
        );
        //await AuthService.newaccount(dataToSend);
        setMessage(
          'This new user is now part of the HVA family! ' +
            values.name +
            ', welcome aboard!'
        );
        setMessageType('success');

        // Reset form except the checkbox with role.id=3
        const roleWithId3 = roles.find(role => role.id === 3);
        resetForm();
        setActivationCode(false);
        if (roleWithId3) {
          setSelectedRoleNames([roleWithId3.rolename]);
        } else {
          setSelectedRoleNames([]);
        }
      } catch (error) {
        setMessage(
          'I apologize, but it seems there was a hiccup! Please review USERNAME and EMAIL.'
        );
        setMessageType('error');
      }
      setModalVisible(true);
    }
  });

  const handleCheckboxChange = (role: Role) => {
    if (role.id === 3) return; // Do nothing if role id is 3

    setSelectedRoleNames(prevSelectedRoleNames =>
      prevSelectedRoleNames.includes(role.rolename)
        ? prevSelectedRoleNames.filter(name => name !== role.rolename)
        : [...prevSelectedRoleNames, role.rolename]
    );
  };

  useEffect(() => {
    formik.setFieldValue('roles', selectedRoleNames);
  }, [selectedRoleNames]);

  const closeModal = () => {
    setModalVisible(false);
  };

  return (
    <div>
      <form className="mb-4" onSubmit={formik.handleSubmit}>
        <div>
          <label className="form-label">First name</label>
          <input
            type="text"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
          />
          {formik.touched.name && formik.errors.name ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.name}
            </div>
          ) : null}
        </div>
        <div>
          <label className="form-label">Last name</label>
          <input
            type="text"
            name="lastname"
            value={formik.values.lastname}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
          />
          {formik.touched.lastname && formik.errors.lastname ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.lastname}
            </div>
          ) : null}
        </div>
        <div>
          <label className="form-label">Username</label>
          <input
            type="text"
            name="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
          />
          {formik.touched.username && formik.errors.username ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.username}
            </div>
          ) : null}
        </div>
        <div>
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
          />
          {formik.touched.email && formik.errors.email ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.email}
            </div>
          ) : null}
        </div>
        <div>
          <label className="form-label">Password</label>
          <input
            type="password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="form-control fs-8"
          />
          {formik.touched.password && formik.errors.password ? (
            <div className="alert phoenix-primary text-primary p-0 fs-9 text-end">
              {formik.errors.password}
            </div>
          ) : null}
        </div>
        <label className="form-label pt-3">
          Two Factor Authentication (TFA)
        </label>
        <PhoenixDocCard className={'border border-light'}>
          <PhoenixDocCard.Body>
            <div className="form-check form-switch mb-0">
              <label className="form-check-label">
                <input
                  type="checkbox"
                  value="activation_code"
                  checked={activationCode}
                  onChange={() => setActivationCode(!activationCode)}
                  className="form-check-input"
                />
                Two Factor Authentication is required for this account
              </label>
            </div>
          </PhoenixDocCard.Body>
        </PhoenixDocCard>
        <label className="form-label pt-3">
          Select account authorization level
        </label>
        <PhoenixDocCard className={'border border-light'}>
          <PhoenixDocCard.Body>
            {roles.map(role => (
              <div key={role.id} className="form-check form-switch">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    value={role.rolename}
                    checked={selectedRoleNames.includes(role.rolename)}
                    onChange={() => handleCheckboxChange(role)}
                    disabled={role.id === 3} // Disable if role id is 3
                    className="form-check-input"
                  />
                  {role.description}
                </label>
              </div>
            ))}
          </PhoenixDocCard.Body>
        </PhoenixDocCard>
        <div className=" pt-4 ">
          <button type="submit" className="btn btn-primary btn-block p-2 px-4">
            <FontAwesomeIcon icon={faUserPlus} className="me-2 fs-8" />
            Create New Account
          </button>
        </div>
      </form>
      <FormResultMessage
        show={modalVisible}
        message={message}
        messageType={messageType}
        logout={false}
        onClose={closeModal}
      />
    </div>
  );
};

export default NewUserForm;
